import React, { useState } from "react";
import {
  SafeAreaView,
  View,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { router } from "expo-router";
import { FontFamilies } from "@/constants/Typography";
import { AppText } from "@/components/AppText";
import Header from "@/components/ui/Header";
import { showError, showSuccess } from "@/utils/toast";
import Select from "@/components/Select";
import { Input } from "@/components/Input";
import { US_STATES } from "@/constants/data";
import { useUserProfile, useUpdateUserProfile } from "@/store/userProfile";
import useUserStore from "@/store/user";

interface ProfileForm {
  firstName: string;
  lastName: string;
  address: string;
  aptSuite: string;
  city: string;
  state: string;
  zipCode: string;
  phoneNumber: string;
  additionalDetails: string;
}

const ProfileInfoScreen: React.FC = () => {
  const insets = useSafeAreaInsets();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const updateProfileMutation = useUpdateUserProfile();
  const email = useUserStore((state) => state.userProfile?.email);

  const [form, setForm] = useState<ProfileForm>({
    firstName: profile?.firstName || "",
    lastName: profile?.lastName || "",
    address: profile?.address || "",
    aptSuite: profile?.aptSuite || "",
    city: profile?.city || "",
    state: profile?.state || "",
    zipCode: profile?.zipCode || "",
    phoneNumber: profile?.phoneNumber || "",
    additionalDetails: profile?.additionalDetails || "",
  });

  // Update form when profile data loads
  React.useEffect(() => {
    if (profile) {
      setForm({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        address: profile.address || "",
        aptSuite: profile.aptSuite || "",
        city: profile.city || "",
        state: profile.state || "",
        zipCode: profile.zipCode || "",
        phoneNumber: profile.phoneNumber || "",
        additionalDetails: profile.additionalDetails || "",
      });
    }
  }, [profile]);

  const handleInputChange = (field: keyof ProfileForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!form.firstName.trim()) {
      showError("First name is required");
      return false;
    }
    if (!form.lastName.trim()) {
      showError("Last name is required");
      return false;
    }
    if (!form.address.trim()) {
      showError("Address is required");
      return false;
    }
    if (!form.city.trim()) {
      showError("City is required");
      return false;
    }
    if (!form.state.trim()) {
      showError("State is required");
      return false;
    }
    if (!form.zipCode.trim()) {
      showError("Zip code is required");
      return false;
    }
    if (!form.phoneNumber.trim()) {
      showError("Phone number is required");
      return false;
    }
    return true;
  };

  const handleConfirm = async () => {
    if (!validateForm()) return;

    try {
      await updateProfileMutation.mutateAsync({
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
        address: form.address,
        aptSuite: form.aptSuite,
        city: form.city,
        state: form.state,
        zipCode: form.zipCode,
        additionalDetails: form.additionalDetails,
      });

      showSuccess("Profile updated successfully!");
      router.back();
    } catch (error) {
      console.error("Error updating profile:", error);
      showError("Failed to update profile. Please try again.");
    }
  };

  const handleCancel = () => {
    router.back();
  };

  const bottomActionsStyle = [
    styles.bottomActions,
    { paddingBottom: insets.bottom === 0 ? 16 : insets.bottom },
  ];

  const isLoading = profileLoading || updateProfileMutation.isPending;

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Bill details" onBackPress={handleCancel} />

      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Header Section */}
          <View style={styles.headerSection}>
            <AppText style={styles.headerTitle}>Bill details</AppText>
            <AppText style={styles.headerDescription}>
              Review your bill information below. We&apos;ll use this only to
              negotiate lower rates with your provider.
            </AppText>
          </View>

          {/* Form Section */}
          <View style={styles.formSection}>
            {/* Name Row */}
            <View style={styles.row}>
              <Input
                placeholder="First Name"
                label="First Name"
                containerStyle={styles.inputGrow}
                style={styles.input}
                required
                value={form.firstName}
                onChangeText={(text) => handleInputChange("firstName", text)}
              />
              <Input
                placeholder="Last Name"
                label="Last Name"
                containerStyle={styles.inputGrow}
                style={styles.input}
                required
                value={form.lastName}
                onChangeText={(text) => handleInputChange("lastName", text)}
              />
            </View>

            {/* Address Row */}
            <View style={styles.row}>
              <Input
                placeholder="Address"
                label="Address"
                containerStyle={styles.inputGrow}
                style={styles.input}
                required
                value={form.address}
                onChangeText={(text) => handleInputChange("address", text)}
              />
              <Input
                placeholder="Apt/Suite"
                label="Apt/Suite"
                containerStyle={styles.inputGrow0}
                style={styles.input}
                value={form.aptSuite}
                onChangeText={(text) => handleInputChange("aptSuite", text)}
              />
            </View>

            {/* City Row */}
            <Input
              placeholder="City"
              label="City"
              containerStyle={styles.inputGrow}
              style={styles.input}
              required
              value={form.city}
              onChangeText={(text) => handleInputChange("city", text)}
            />

            {/* State and Zip Row */}
            <View style={styles.row}>
              <Select
                options={US_STATES.map((state) => ({
                  label: state,
                  value: state,
                }))}
                onSelect={(value) =>
                  handleInputChange("state", value.value as string)
                }
                required
                placeholder="State"
                label="State"
                containerStyle={styles.inputGrow}
                style={styles.input}
                value={form.state}
              />
              <Input
                placeholder="Zip code"
                label="Zip code"
                containerStyle={styles.inputGrow}
                style={styles.input}
                required
                keyboardType="numeric"
                value={form.zipCode}
                onChangeText={(text) => handleInputChange("zipCode", text)}
              />
            </View>

            {/* Email Row */}
            <Input
              placeholder="Email address"
              label="Email address"
              containerStyle={styles.inputGrow}
              style={styles.input}
              disabled
              value={email}
            />

            {/* Phone Row */}
            <Input
              placeholder="Phone Number"
              label="Phone Number"
              containerStyle={styles.inputGrow}
              style={styles.input}
              required
              keyboardType="phone-pad"
              value={form.phoneNumber}
              onChangeText={(text) => handleInputChange("phoneNumber", text)}
            />

            {/* Additional Details */}
            <Input
              placeholder="e.g., Account PIN: 1234, Customer ID: ABC123, Last 4 SSN: 5678, or special instructions for negotiation"
              label="Additional Details"
              containerStyle={styles.inputGrow}
              style={[styles.input, styles.textArea]}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              value={form.additionalDetails}
              onChangeText={(text) => handleInputChange("additionalDetails", text)}
            />
          </View>
        </ScrollView>

        {/* Bottom Actions */}
        <View style={bottomActionsStyle}>
          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.confirmButton, isLoading && styles.disabledButton]}
              onPress={handleConfirm}
              activeOpacity={0.8}
              disabled={isLoading}
            >
              <AppText style={styles.confirmButtonText}>
                {isLoading ? "Saving..." : "Confirm & Continue"}
              </AppText>
            </TouchableOpacity>
          </View>

          <View style={styles.cancelContainer}>
            <TouchableOpacity onPress={handleCancel} activeOpacity={0.8}>
              <AppText style={styles.cancelText}>Cancel</AppText>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  keyboardAvoid: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 150,
  },
  headerSection: {
    gap: 8,
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#161D14",
    lineHeight: 33,
  },
  headerDescription: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    lineHeight: 23,
  },
  formSection: {
    paddingTop: 16,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  inputGrow: {
    flexGrow: 1,
  },
  inputGrow0: {
    flexGrow: 0,
  },
  input: {
    backgroundColor: "#fff",
    borderColor: "#D8D9DB",
  },
  textArea: {
    height: 100,
    paddingTop: 12,
  },
  bottomActions: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "#FFFFFF",
    alignItems: "center",
    gap: 12,
    paddingTop: 24,
    borderTopWidth: 1,
    borderTopColor: "#F5F5F5",
  },
  buttonContainer: {
    width: 343,
  },
  confirmButton: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingVertical: 16.5,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
    height: 58,
  },
  disabledButton: {
    opacity: 0.6,
  },
  confirmButtonText: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    lineHeight: 27,
    color: "#FFFFFF",
  },
  cancelContainer: {
    alignItems: "center",
  },
  cancelText: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#414141",
  },
});

export default ProfileInfoScreen;
